import { cached, tracked } from '@glimmer/tracking';

import type { APIData, RequestError, ValidationError } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyObject, equal, prettyJsonStringify, uniq } from 'webapp/utils/common';

import CustomQuerySetupWizardApiMetricBuilderComponent from 'webapp/components/custom-query/setup/wizard/api-metric-builder';

import HeaderOrQueryParameter from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder/header-or-query-parameter';
import JSONStructure from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder/json-structure';
import type { PreviewErrorData } from 'webapp/models-custom/custom-metric-setup-form/base';
import CustomMetricSetupFormBase from 'webapp/models-custom/custom-metric-setup-form/base';
import type { ValueSelected } from 'webapp/models-custom/custom-metric-setup-form/item/field';
import type StatusObject from 'webapp/models-custom/status-object';

import type { RSjqXHR, RSPromise } from 'webapp/services/request';

const BASE_URL_PLACEHOLDER = '{baseUrl}';

export default class CustomMetricSetupFormApiMetricBuilder extends CustomMetricSetupFormBase {
	@tracked override originalSettings: {
		builder: APIData.CustomQuery.APIMetricBuilder.BuilderData;
		settings: APIData.CustomQuery.APIMetricBuilder.SettingsData;
	} | null = null;

	// options and defaults
	baseUrl = '';
	httpMethodOptions: string[] = [];
	dateTimeFormatOptions: APIData.CustomQuery.APIMetricBuilder.BuilderDateFormatOption[] = [];
	paginationOptions: APIData.CustomQuery.APIMetricBuilder.BuilderPaginationOption[] = [];

	@tracked apiRefLink = '';

	// current settings
	@tracked httpMethod = '';
	@tracked url = '';
	@tracked body: SomeDataObject | null = null;
	@tracked isBodyValid = true;
	@tracked dateTimeFormat = '';
	@tracked pagination = ''; // will send empty as null
	@tracked queryParameters: HeaderOrQueryParameter[] = [];
	@tracked headers: HeaderOrQueryParameter[] = [];

	@tracked extractionExpression = '';
	@tracked metricDefinition: APIData.CustomQuery.MetricDefinitionValue | null = null;

	wizardComponent = CustomQuerySetupWizardApiMetricBuilderComponent;

	get isWizardFlowSupported(): boolean {
		return true;
	}

	get queryParametersOutput(): APIData.CustomQuery.APIMetricBuilder.SettingKeyValuePair[] {
		return this.queryParameters.filter((item) => !item.isEmpty).map((item) => item.output);
	}

	get headersOutput(): APIData.CustomQuery.APIMetricBuilder.SettingKeyValuePair[] {
		return this.headers.filter((item) => !item.isEmpty).map((item) => item.output);
	}

	get hasPagination(): boolean {
		return this.paginationOptions.length > 0;
	}

	get hasDateTimeFormat(): boolean {
		return this.dateTimeFormatOptions.length > 0;
	}

	get helpArticleUrl(): string {
		return this.query.sourceInfo?.qbLink || '';
	}

	get userEnteredPath(): string {
		// the user-entered part, w/o BASE_URL_PLACEHOLDER
		return this.url.replace(BASE_URL_PLACEHOLDER, '');
	}

	set userEnteredPath(value: string) {
		this.url = `${BASE_URL_PLACEHOLDER}${value}`;
	}

	// ------- Initializing --------

	override initData(data: APIData.CustomQuery.BuilderAPIMetricBuilderResponse) {
		// data = window.dbx_mock['GET /d/customqueries/<qid>/builder/<sid> - apimb'];

		// parsing builder props that are just options
		this.apiRefLink = data.builder.apiRefLink || '';
		this.httpMethodOptions = data.builder.httpMethod;
		this.baseUrl = data.builder.baseUrl || '';
		this.dateTimeFormatOptions = data.builder.dateTimeFormats || [];
		this.paginationOptions = data.builder.pagination || [];

		super.initData(data);

		this.transformsData = data.transformations || null;

		const setFromSettings = !this.query.isNew || this.query.cloneFrom;
		if (setFromSettings) {
			this.setInitialValues(data.settings, data.builder);
		} else {
			this.setDefaultValues(data.builder);
		}

		this.lastQueriedPreviewPayload = {
			settings: this.prepareSettings(),
			transformations: null,
		};

		if (setFromSettings) {
			this.triggerGetPreview(); // get raw data
		}
	}

	private setDefaultValues(builderData: APIData.CustomQuery.APIMetricBuilder.BuilderData): void {
		// set props from the defaults (builder)
		this.httpMethod = this.httpMethodOptions[0]!;
		this.body = builderData.body || null;
		this.url = BASE_URL_PLACEHOLDER;
		this.dateTimeFormat = this.dateTimeFormatOptions[0]?.id || '';
		this.pagination = this.paginationOptions[0]?.id || '';

		this.headers = builderData.headers.map((headerData) => {
			return new HeaderOrQueryParameter(headerData);
		});
		this.queryParameters = builderData.query.map((queryData) => {
			return new HeaderOrQueryParameter(queryData);
		});
	}

	private setInitialValues(
		settings: APIData.CustomQuery.APIMetricBuilder.SettingsData,
		builderData: APIData.CustomQuery.APIMetricBuilder.BuilderData,
	): void {
		this.httpMethod = settings.httpMethod;
		this.body = settings.body || null;
		this.url = settings.url;
		this.dateTimeFormat = settings.dateTimeFormat || '';
		this.pagination = settings.pagination || '';

		this.headers = settings.headers.map((headerData) => {
			const defaultHeader = builderData.headers?.find((item) => item.key === headerData.key),
				param = new HeaderOrQueryParameter(headerData);
			param.hidden = !!defaultHeader?.hidden;
			param.disabled = !!defaultHeader?.disabled;
			return param;
		});
		this.queryParameters = settings.query.map((queryData) => {
			const defaultHeader = builderData.query?.find((item) => item.key === queryData.key),
				param = new HeaderOrQueryParameter(queryData);
			param.hidden = !!defaultHeader?.hidden;
			param.disabled = !!defaultHeader?.disabled;
			return param;
		});

		this.extractionExpression = settings.extractionExpression;
		// prepareSettings sends {} while no metric definition is set; one saved that way comes back as {} and is kept
		this.metricDefinition =
			(settings.metricDefinition as APIData.CustomQuery.MetricDefinitionValue | undefined) ||
			null;
	}

	// ------ current form status & handlers ---------

	get isFormReady(): boolean {
		return !this.isLoadingBuilder;
	}

	get isPreviewButtonDisabled(): boolean {
		return !this.isFormReady;
	}

	override get previewButtonText(): string {
		return this.shouldPreviewButtonTriggerGetRawData ? 'Execute request' : 'Preview data';
	}

	override get isPreviewButtonLoading(): boolean {
		return this.isLoadingRawData || this.isLoadingPrePreview || this.isLoadingPreview;
	}

	override get isSaveButtonDisabled(): boolean {
		return (
			super.isSaveButtonDisabled ||
			this.isPreviewButtonDisabled ||
			!this.rawData ||
			!this.extractionExpression
		);
	}

	get isLoadingRawData(): boolean {
		return !!this.rawDataPromise;
	}

	get isLoadingPrePreview(): boolean {
		return !!this.prePreviewPromise;
	}

	get shouldPreviewButtonTriggerGetRawData(): boolean {
		return !this.rawData || this.isGeneralSettingsDirty;
	}

	get isBodySupported(): boolean {
		return this.httpMethod !== 'GET';
	}

	private get isGeneralSettingsDirty(): boolean {
		let isDirty = false;
		if (this.lastQueriedRawDataPayload) {
			const lastQueriedGeneralSettings: SomeDataObject = copyObject(
					this.lastQueriedRawDataPayload,
				),
				currentSettings: SomeDataObject = this.prepareSettings();
			delete lastQueriedGeneralSettings.extractionExpression;
			delete currentSettings.extractionExpression;
			isDirty = !equal(lastQueriedGeneralSettings, currentSettings);
		}
		return isDirty;
	}

	get isExpressionDirty(): boolean {
		let isDirty = false;
		if (this.lastQueriedPrePreviewPayload) {
			const lastQueriedExpression = this.lastQueriedPrePreviewPayload.extractionExpression,
				currentExpression = this.extractionExpression;
			isDirty = lastQueriedExpression !== currentExpression;
		}
		return isDirty;
	}

	override triggerGetPreview(): void {
		void this.validate().then((isValid) => {
			if (isValid) {
				if (this.shouldPreviewButtonTriggerGetRawData) {
					void this.getRawData();
				} else {
					void this.queryPreview();
				}
			}
		});
	}

	checkAreItemsWithDefinesRawIdChanged(): boolean {
		return true;
	}

	updateExternalField(_fieldId: string, _fieldValue: ValueSelected): void {
		// does not happen here
		return;
	}

	// -------- raw response data (execute)  ---------

	@tracked rawData: unknown = null; // the response of the user's API, any JSON
	@tracked private rawDataPromise: Promise<unknown> | null = null;
	@tracked
	private lastQueriedRawDataPayload: APIData.CustomQuery.APIMetricBuilder.ExecuteRequest | null =
		null;

	@cached
	get jsonStructure(): JSONStructure | null {
		if (!this.rawData) {
			return null;
		}
		return new JSONStructure(this.rawData);
	}

	getRawData(): Promise<unknown> {
		// if request already in progress, cancel it
		const xhr = (this.rawDataPromise as RSPromise<unknown>)?.jqPromise as RSjqXHR | undefined;
		if (xhr && xhr?.readyState !== 4) {
			xhr.abort();
		}

		const settings: APIData.CustomQuery.APIMetricBuilder.ExecuteRequest =
			this.prepareSettings();
		delete (settings as SomeDataObject).extractionExpression;
		this.lastQueriedRawDataPayload = settings;

		const promise =
			this.requestService.api<APIData.CustomQuery.APIMetricBuilder.ExtractDataResponse>(
				'POST',
				`${this.builderUrl}/extract-data`,
				{ settings },
				{ timeout: 5 * 60 * 1000 },
			);
		// const promise = new Promise((resolve, _reject) => { setTimeout(resolve, 2000); });
		promise
			.then(
				(reply) => {
					// reply = {"statusCode": 200,"body":{"sales":[{"amount":12,"date":"2022-02-12"},{"amount":8,"date":"2022-02-14"},{"amount":2,"date":"2022-02-15"}]}};

					// huuuge (16k lines) payload below for perf testing
					// reply = window.dbx_mock['POST /d/customqueries/<qid>/builder/<sid>/extract-data'];

					// supersmall, no recommendations

					// reply = {"statusCode":200,"body":{"ok":true,"users":["UC1U2MW86","U01M6TAMPFS","U03BJPE2668"]}};

					this.rawData = reply?.body ?? null;

					// reset so user is forced to query pre-preview
					this.prePreviewData = null;
					this.lastQueriedPrePreviewPayload = null;

					this.updateExtractionExpression();
				},
				(errorData) => {
					this.rawData = null;
					this.lastQueriedRawDataPayload = null;

					throw errorData;
				},
			)
			.catch((errorData: RequestError) => {
				const responseJSON = errorData.obj?.responseJSON;

				// test data

				// const responseJSON = {"statusCode":200,"body":[{"name":"API request failed","message":"Request executed against API failed.","apiResponse":{"ok":false,"error":"unknown_method","req_method":"usergrou<br>ps.listyui"},"statusCode":400}]};

				// const responseJSON = {"statusCode":200,"body":[{"name":"API request failed","message":"Request executed against API failed.","apiResponse":"Some <h1>Cool</h1> html","statusCode":400}]};

				const parsedErrorData: SomeDataObject = {};
				if (responseJSON?.body) {
					interface ErrorItem {
						name?: string;
						message?: string;
						apiResponse?: string | SomeDataObject;
					}

					let html: string | JQuery<HTMLElement> = '';
					(responseJSON.body || []).forEach((errorItem: ErrorItem) => {
						// "escaping" returned texts
						const tmp = $('<div/>');
						if (errorItem.name) {
							tmp.append($('<strong/>').text(errorItem.name));
							tmp.append('<br>');
						}
						if (errorItem.message) {
							tmp.append(document.createTextNode(errorItem.message));
							tmp.append('<br>');
						}
						if (errorItem.apiResponse) {
							// can be string or json obj
							let apiResponse = errorItem.apiResponse;
							if (typeof apiResponse !== 'string') {
								apiResponse = prettyJsonStringify(apiResponse);
							}
							tmp.append($('<pre/>').text(apiResponse));
						}
						html = tmp;
					});

					parsedErrorData.html = html;
				}

				this.onError(parsedErrorData, 'An error has occurred.');
			})
			.finally(() => {
				this.rawDataPromise = null;
			});

		this.rawDataPromise = promise;

		return promise;
	}

	updateExtractionExpression(): void {
		if (this.extractionExpression) {
			// don't overwrite existing
			return;
		}

		const jsonStructure = this.jsonStructure;
		if (!jsonStructure) {
			return;
		}

		// must have metric
		if (!jsonStructure.rowMetric) {
			return;
		}

		const extractionExpression = this.jsonStructure?.generateJsonAta();
		if (extractionExpression && extractionExpression !== '{}') {
			this.extractionExpression = extractionExpression;
		}
	}

	// -------- extracted data (pre-preview)  ---------

	@tracked prePreviewData: APIData.CustomQuery.APIMetricBuilder.PrePreviewResponseItem[] | null =
		null;
	@tracked private prePreviewPromise: Promise<unknown> | null = null;
	@tracked
	private lastQueriedPrePreviewPayload: APIData.CustomQuery.APIMetricBuilder.PrePreviewRequest | null =
		null;

	override get previewDataForBottomTable(): APIData.CustomQuery.PreviewDataForBottomTable | null {
		if (this.prePreviewData) {
			const data: APIData.CustomQuery.PreviewDataForBottomTable = {
				headers: [{ isDate: true }, { isMetric: true }],
				rows: [],
				totalRows: [],
			};
			const totals: SomeDataObject = {};
			let hasDimensions = false;
			this.prePreviewData.forEach((item, k) => {
				if (!k && typeof item.dimension !== 'undefined') {
					hasDimensions = true;
				}
				const cellData: APIData.CustomQuery.PreviewCell[] = [];
				cellData.push(
					{
						current: item.date,
					},
					{
						current: item.value,
					},
				);
				if (hasDimensions) {
					cellData.push({
						current: item.dimension || '',
					});
				}
				data.rows.push(cellData);

				// count totals
				const dimension = item.dimension || '';
				if (typeof totals[dimension] === 'undefined') {
					totals[dimension] = 0;
				}
				totals[dimension] += item.value || 0;
			});
			if (hasDimensions) {
				data.headers.push({
					isAttribute: true,
				});
			}

			Object.keys(totals).forEach((dimension) => {
				data.totalRows.push(['-', totals[dimension], dimension]);
			});

			return data;
		} else {
			return null;
		}
	}

	getPrePreviewData(): Promise<boolean> {
		// if request already in progress, cancel it
		const xhr = (this.prePreviewPromise as RSPromise<unknown>)?.jqPromise as
			RSjqXHR | undefined;
		if (xhr && xhr.readyState !== 4) {
			xhr.abort();
		}

		const data: APIData.CustomQuery.APIMetricBuilder.PrePreviewRequest = {
			data: this.rawData!,
			extractionExpression: this.extractionExpression,
		};

		this.lastQueriedPrePreviewPayload = data;

		return new Promise((resolve, _reject) => {
			const promise = this.requestService.api<
				APIData.CustomQuery.APIMetricBuilder.PrePreviewResponseItem[]
			>('POST', `${this.builderUrl}/preview-selection`, data, { timeout: 5 * 60 * 1000 });
			// const promise = new Promise((resolve, _reject) => { setTimeout(resolve, 3000); });
			promise
				.then(
					(reply) => {
						// reply = [{"value":15,"date":"2022-09-09T06:49:07.057-07:00","dimension":"bla"},{"value":2,"date":"2022-09-09T06:49:16.173-07:00","dimension":"gg"},{"value":4,"date":"2022-09-09T06:48:58.827-07:00","dimension":"bla"}];

						this.prePreviewData = reply;

						resolve(true);
					},
					(errorData) => {
						this.prePreviewData = null;
						this.lastQueriedPrePreviewPayload = null;

						resolve(false);

						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
				.finally(() => {
					this.prePreviewPromise = null;
				});

			this.prePreviewPromise = promise;
		});
	}

	// -------- prepared / aggregated data (preview)  ---------

	@tracked override lastQueriedPreviewPayload:
		APIData.CustomQuery.APIMetricBuilder.PreviewRequest | Record<string, never> | null = null; // Record<string, never> === {}

	prepareSettings(): APIData.CustomQuery.APIMetricBuilder.SettingsData {
		if (this.prepareSettingsFn) {
			return this.prepareSettingsFn() as APIData.CustomQuery.APIMetricBuilder.SettingsData;
		}
		const data: APIData.CustomQuery.APIMetricBuilder.SettingsData = {
			httpMethod: this.httpMethod,
			url: this.url,
			body: this.isBodySupported ? this.body : null,
			dateTimeFormat: this.dateTimeFormat,
			pagination: this.pagination || null,
			query: this.queryParametersOutput,
			headers: this.headersOutput,
			extractionExpression: this.extractionExpression,
			metricDefinition: this.metricDefinition || {},
		};
		return data;
	}

	override getPreviewPayload(): APIData.CustomQuery.APIMetricBuilder.PreviewRequest {
		const data =
			super.getPreviewPayload() as APIData.CustomQuery.APIMetricBuilder.PreviewRequest;
		if (this.prePreviewData) {
			data.data = this.prePreviewData;
		}
		return data;
	}

	override queryPreview(): Promise<unknown> {
		if (!this.isValid) {
			return Promise.resolve();
		}

		let prePromise: Promise<boolean>;

		// skip doing pre-preview if already have pre-preview and nothing has changed
		const skipPrePreview =
			!!this.lastQueriedPrePreviewPayload &&
			!this.isGeneralSettingsDirty &&
			!this.isExpressionDirty;
		if (skipPrePreview) {
			prePromise = Promise.resolve(true);
		} else {
			if (!this.extractionExpression) {
				this.errors = [
					{
						name: 'general',
						message: 'JSONata expression is mandatory.',
					},
				];
				return Promise.resolve();
			}
			prePromise = this.getPrePreviewData();
		}

		this.isLoadingPreview = true;

		return prePromise
			.then((isSuccess: boolean) => {
				if (isSuccess) {
					return super.queryPreview();
				} else {
					return;
				}
			})
			.finally(() => {
				this.isLoadingPreview = false;
			});
	}

	// -------- validation & error handling ---------

	get isValidAndValidated() {
		return this.errors.length === 0;
	}

	get isValidatingWithRequest() {
		return false;
	}

	handlePreviewErrorFromStatusObjects(statusObjects: StatusObject[]): void {
		const dataErrorText = statusObjects
			.map((item) => item.statusTitleAndDescription)
			.join('<br>');

		if (dataErrorText) {
			this.handlePreviewError({ html: dataErrorText });
		}
	}

	handlePreviewError(errorData: PreviewErrorData<string>) {
		const message = errorData.html || this.getFormattedError(errorData.errors || [], true);

		this.errors = [
			{
				name: 'general',
				message,
			},
		];
	}

	validate() {
		const errors: ValidationError[] = [];

		if (!this.userEnteredPath) {
			errors.push({ name: 'general', message: 'Invalid URL.' });
		}

		// query params and headers:
		{
			// leave max one empty
			const emptyQueryParams = this.queryParameters.filter((item) => item.isEmpty),
				emptyHeaders = this.headers.filter((item) => item.isEmpty);

			for (let i = 0; i < emptyQueryParams.length - 1; i++) {
				this.queryParameters = this.queryParameters.filter(
					(q) => q !== emptyQueryParams[i],
				);
			}
			for (let i = 0; i < emptyHeaders.length - 1; i++) {
				this.headers = this.headers.filter((h) => h !== emptyHeaders[i]);
			}
		}

		// validate only those that are not empty
		const queryParams = this.queryParameters.filter((item) => !item.isEmpty),
			headers = this.headers.filter((item) => !item.isEmpty);

		// trigger error on duplicate or valueless
		if (queryParams.filter((item) => !item.isValid).length) {
			errors.push({ name: 'general', message: 'Incomplete parameters.' });
		}

		if (headers.filter((item) => !item.isValid).length) {
			errors.push({ name: 'general', message: 'Incomplete headers.' });
		}

		const paramOutputKeys = queryParams.map((item) => item.key);
		if (paramOutputKeys.length !== uniq(paramOutputKeys).length) {
			errors.push({ name: 'general', message: 'Duplicate parameters.' });
		}

		const headerOutputKeys = headers.map((item) => item.key);
		if (headerOutputKeys.length !== uniq(headerOutputKeys).length) {
			errors.push({ name: 'general', message: 'Duplicate headers.' });
		}

		if (!this.isBodyValid) {
			errors.push({ name: 'general', message: 'Body payload invalid.' });
		}

		this.errors = errors;

		return Promise.resolve(this.isValidAndValidated);
	}

	// -------- field statuses ---------

	handleItemAction(_item: string, _action: string): void {
		// not important here
	}

	// -------- tacking etc ----------

	onWizardFlowToggle(isWizardFlow: boolean): void {
		const eventName = isWizardFlow
			? TrackingEvent.QUERY_SWITCH_TO_WIZARD
			: TrackingEvent.QUERY_SWITCH_TO_MANUAL;
		this.analyticsService.event(eventName, { type: this.query.trackingWizardType });
	}
}
